import { Component, OnInit, inject } from '@angular/core';
import { Plan } from '../../../core/models/common/plan';
import { fromStore } from 'angular/app/core/store';

import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { TranslocoService } from '@jsverse/transloco';

type FeatureCell = boolean | 'unlimited' | 'extra';

interface FeatureRow {
  /** Identifiant stable, suffixe de la clé de traduction `pricing.feature.{id}`. */
  id: string;
  availability: Partial<Record<Plan, FeatureCell>>;
}

@Component({
  selector: 'app-pricing-page',
  template: `
    <div class="container pt-10 xl:pt-20">
      <app-plan-calculator />

      <app-plans class="mt-20 block xl:mt-40"></app-plans>

      <div class="mt-20 xl:mt-40">
        <h2 class="text-center">{{ 'pricing.comparator.title' | transloco }}</h2>

        <div class="sticky top-14 mt-16 bg-white px-6 pb-3 lg:px-16 lg:pt-16">
          <div
            class="grid grid-cols-2 items-end font-bold uppercase md:grid-cols-4"
          >
            <div class="hidden md:block md:pl-6">{{
              'pricing.comparator.option' | transloco
            }}</div>
            <div class="hidden text-center md:block">
              <img
                src="/assets/img/free-plan.svg"
                [alt]="'pricing.plan.free.img-alt' | transloco"
                class="mx-auto w-28"
              />
            </div>
            <div class="hidden text-center md:block">
              <img
                src="/assets/img/annual-subscription.svg"
                [alt]="'pricing.plan.annual.img-alt' | transloco"
                class="mx-auto w-28"
              />
            </div>
            <div class="hidden text-center md:block">
              <img
                src="/assets/img/commission-based.svg"
                [alt]="'pricing.plan.commission.img-alt' | transloco"
                class="mx-auto w-28"
              />
            </div>
          </div>
        </div>

        <div class="md:hidden">
          <app-plan-selector></app-plan-selector>
        </div>

        <div class="mb:mt-0 space-y-4 bg-white pt-10 pb-4 lg:px-16 lg:pb-16">
          @for (feature of features; track feature.id; let i = $index) {
            <div
              class="grid grid-cols-2 items-center gap-2 rounded-3xl px-4 md:grid-cols-4 md:gap-0 md:rounded-full md:px-6"
              [ngClass]="{
                'bg-light py-4': i % 2 === 0,
              }"
            >
              <div class="col-span-1">{{
                'pricing.feature.' + feature.id | transloco
              }}</div>
              @for (plan of plansOptions; track plan.value) {
                <div
                  class="col-span-1 hidden text-center md:block"
                  [innerHTML]="content(feature, plan.value)"
                >
                </div>
              }
              <div
                class="col-span-1 text-center md:hidden"
                [innerHtml]="content(feature, selectedPlan$ | async)"
              >
              </div>
            </div>
          }
        </div>
      </div>
    </div>

    <app-spacer size="20"></app-spacer>
  `,
  styles: [],
  standalone: false,
})
export class PricingPageComponent implements OnInit {
  private translocoService = inject(TranslocoService);

  plansOptions = Object.values(Plan)
    .filter((plan) => plan !== Plan.UrbaApp)
    .map((plan) => ({
      value: plan,
    }));
  selectedPlan$: Observable<Plan> = this.store.select(fromStore.selectPlan);
  features: FeatureRow[] = [
    {
      id: 'users',
      availability: {
        [Plan.Free]: 'unlimited',
        [Plan.AnnualSubscription]: 'unlimited',
        [Plan.CommissionBased]: 'unlimited',
      },
    },
    {
      id: 'internal-bookings',
      availability: {
        [Plan.Free]: 'unlimited',
        [Plan.AnnualSubscription]: 'unlimited',
        [Plan.CommissionBased]: 'unlimited',
      },
    },
    {
      id: 'paid-rentals',
      availability: {
        [Plan.AnnualSubscription]: true,
        [Plan.CommissionBased]: true,
      },
    },
    {
      id: 'automated-billing',
      availability: {
        [Plan.AnnualSubscription]: true,
        [Plan.CommissionBased]: true,
      },
    },
    {
      id: 'full-room-equipment-management',
      availability: {
        [Plan.Free]: true,
        [Plan.AnnualSubscription]: true,
        [Plan.CommissionBased]: true,
      },
    },
    {
      id: 'recurring-bookings',
      availability: {
        [Plan.Free]: true,
        [Plan.AnnualSubscription]: true,
        [Plan.CommissionBased]: true,
      },
    },
    {
      id: 'staff-notifications',
      availability: {
        [Plan.Free]: true,
        [Plan.AnnualSubscription]: true,
        [Plan.CommissionBased]: true,
      },
    },
    {
      id: 'extra-equipment-rentals',
      availability: {
        [Plan.AnnualSubscription]: true,
        [Plan.CommissionBased]: true,
      },
    },
    {
      id: 'pricing-groups',
      availability: {
        [Plan.AnnualSubscription]: true,
        [Plan.CommissionBased]: true,
      },
    },
    {
      id: 'automated-cancellation',
      availability: {
        [Plan.AnnualSubscription]: true,
        [Plan.CommissionBased]: true,
      },
    },
    {
      id: 'communication-templates',
      availability: {
        [Plan.AnnualSubscription]: true,
        [Plan.CommissionBased]: true,
      },
    },
    {
      id: 'promo-codes',
      availability: {
        [Plan.AnnualSubscription]: true,
        [Plan.CommissionBased]: true,
      },
    },
    {
      id: 'two-factor-auth',
      availability: {
        [Plan.Free]: true,
        [Plan.AnnualSubscription]: true,
        [Plan.CommissionBased]: true,
      },
    },
    {
      id: 'room-groups',
      availability: {
        [Plan.AnnualSubscription]: true,
        [Plan.CommissionBased]: true,
      },
    },
    {
      id: 'dormakaba-integration',
      availability: {
        [Plan.AnnualSubscription]: 'extra',
        [Plan.CommissionBased]: 'extra',
      },
    },
    {
      id: 'booking-options',
      availability: {
        [Plan.AnnualSubscription]: true,
        [Plan.CommissionBased]: true,
      },
    },
    {
      id: 'support',
      availability: {
        [Plan.AnnualSubscription]: true,
        [Plan.CommissionBased]: true,
      },
    },
  ];

  constructor(private store: Store) {}

  ngOnInit(): void {}

  content(feature: FeatureRow, plan: string | Plan | null): string {
    const value = plan ? feature.availability[plan as Plan] : undefined;
    if (value === true) {
      return '<i class="fa-solid fa-circle-check text-primary-500"></i>';
    } else if (typeof value === 'string') {
      // Valeur texte ('unlimited' / 'extra') traduite via transloco
      return this.translocoService.translate('pricing.value.' + value);
    } else {
      return '-';
    }
  }
}
